// Animations
import scrollToElement from './principles/animations/scrollTo';

// Selectors
import navbarHeight from './principles/selectors/navbarHeight';

import { validateField } from './principles/validation/validateForm';
import { validateAllFields } from './principles/validation/validateForm';
import { conditionedValue } from './principles/form/serialize';
import * as vashonValidation from './principles/vashon/vashon-validation';
import * as eventTracking from './principles/event-tracking';
import * as formNavigation from './principles/leadform-navigation';
import * as formVariables from './principles/leadform-variables';
import * as callbackScheduling from './principles/leadform-callback-scheduling';
import addStartupScript from './principles/addStartupScript';

addStartupScript(() => {
  vashonValidation.setUpFormValidation();
  formNavigation.setUpFormNavigation('.js-leadform', validateAllFields, true);
  formVariables.setUpFormVariables();
  eventTracking.trackEvents();
  callbackScheduling.setupCallbackScheduling();
  initializeVashon();
});

export function panelNumber(panelIndex) {
  return panelIndex + 1;
}

/**
 * returns the target Id of the next Vashon question
 * @param {Vashon question} question
 */
export function getNextTargetId(question) {
  const input = question
    .querySelector('.vashon-question__main')
    .querySelector('select,input');
  const answer = input.value;

  let nextQuestionTargetId;
  if (question.dataset.questionType == 'select') {
    const select = question.querySelector('.vashon-question__main select');
    let targetOption = select.querySelector(
      `option[data-target][value="${answer}"]`
    );

    if(targetOption) {
      nextQuestionTargetId = targetOption.dataset.target;
    } else {
      nextQuestionTargetId = select.closest('.vashon-question').dataset.target;
    }

  } else {
    nextQuestionTargetId = question.dataset.target;
  }

  return nextQuestionTargetId;
}


/**
 * returns all input fields part of the Vashon question
 * @param {Vashon Question/Phrase/Panel} el
 */
export function getQuestionInputs(el){
  return el.querySelectorAll('input,select');
}

/**
 * returns the first input of a Vashon component
 * @param {Vashon question/phrase/panel} el
 */
export function getFirstInput(el){
  return el.querySelector('input,select');
}

/**
 * Returns true if the Vashon component has any inputs
 * @param {*} el
 */
export function hasInputs(el){
  return getQuestionInputs(el).length > 0;
}

/**
 * Gets the next question following the element
 * @param {input DOM Element} el
 */
export function getNextQuestion(el){
  let question;
  if (el.classList.contains('vashon-question')){ question = el; }
  else { question = el.closest('.vashon-question'); }
  const nextTargetId = getNextTargetId(question);
  return document.getElementById(nextTargetId);
}

/**
 * Gets the next input field in the given question
 * @param {input DOM Element} el
 */
export function getNextInputInQuestion(el) {
  const question = el.closest('.vashon-question');
  const allInputs = getQuestionInputs(question);
  const allInputArray = Array.from(allInputs);
  const indexOfInput = allInputArray.indexOf(el);
  if (indexOfInput == -1 || indexOfInput == allInputArray.length - 1)
   { return null; }
  return allInputArray[indexOfInput + 1];
}


/**
 * Get the next input field in the panel
 * @param {Input DOM element} el
 */
export function getNextInputInPanel(el){
  if (isLastInput(el) == false) { return getNextInputInQuestion(el); }
  if (isLastQuestion(el) == false) {
    const nextQuestion = getNextQuestion(el);
    if (hasInputs(nextQuestion)) { return getFirstInput(nextQuestion); }
    if (isLastPhrase(el) == false){ return getFirstInput(getNextPhrase(el)); }
  }
  if (isLastPhrase(el) == false){ return getFirstInput(getNextPhrase(el)); }
  return getContinueButton(el);
}

/**
 * Returns a list of questions following the current element
 * @param {input DOM Element} el
 */
function getRemainingQuestions(el){
  const question = el.closest('.vashon-question');
  const phrase = el.closest('.vashon-phrase');
  const allQuestions = Array.from(phrase.querySelectorAll('.vashon-question'));
  const index = allQuestions.indexOf(question);
  return allQuestions.slice(index + 1);
}

/**
 * Returns a list of phrases following the current element
 * @param {input DOM Element} el
 */
function getRemainingPhrases(el){
  const phrase = el.closest('.vashon-phrase');
  const phraseContainer = el.closest('.vashon-phrase-container');
  const allPhrases = Array.from(phraseContainer.querySelectorAll('.vashon-phrase'));
  const index = allPhrases.indexOf(phrase);
  return allPhrases.slice(index + 1);
}

/**
 * Gets the next phrase the current element is not part of
 * @param {input DOM Element} el
 */
function getNextPhrase(el) {
  const currentPhrase = el.closest('.vashon-phrase');

  let nextPhrase = currentPhrase.nextElementSibling;
  while (nextPhrase != null){
    if(nextPhrase.classList.contains('vashon-phrase')){
      break;
    }
    nextPhrase = nextPhrase.nextElementSibling;
  }
  return nextPhrase;
}

function getFirstQuestion(phrase) {
  return phrase.querySelector('.vashon-question');
}

/**
 * Unblocks the panel following the input element
 * @param {input dom Element} el
 */
function unblockElementsAfterInput(el) {
  if (!isLastInput(el)) { return; }
  if (!isLastQuestion(el)) { return unblockNextQuestion(el); }
  if (!isLastPhrase(el)) { return unblockNextPhrase(el); }
  unblockNextPanel(el);
}

/**
 * Blocks the panel following the input element
 * @param {input dom Element} el
 */
function blockElementsAfterInput(el){
  blockNextQuestions(el);
  blockNextPhrases(el);
  blockNextPanel(el);
}

/**
 * Blocks the given question
 * @param {Vashon question} el
 */
function blockQuestion(question) {
  question.classList.add('vashon-phrase__question--blocked');
}

/**
 * Unblocks the given question
 * @param {Vashon question} el
 */
function unblockQuestion(question) {
  question.classList.remove('vashon-phrase__question--blocked');
}

/**
 * Blocks the given phrase
 * @param {Vashon phrase} el
 */
function blockPhrase(phrase) {
  phrase.classList.add('vashon-phrase--blocked');
}

/**
 * Unblocks the given phrase
 * @param {Vashon phrase} el
 */
function unblockPhrase(phrase) {
  phrase.classList.remove('vashon-phrase--blocked');
}

/**
 * Unhides the continue button on the panel containing the element
 * @param {input DOM Element} el
 */
function unblockNextPanel(el) {
  getContinueButton(el).classList.remove('hidden');
}

/**
 * Returns the continue button for any element on the panel
 */
function getContinueButton(el) {
  const panelIndex = getPanelIndex(el);
  return document
    .getElementsByClassName(`js-panel-${panelNumber(panelIndex)}-continue-button`)[0];
}

/**
 * Unblocks the next phrase following the input element
 * @param {input DOM Element} el
 */
function unblockNextPhrase(el) {
  const phraseToUnblock = getNextPhrase(el);
  if (phraseToUnblock) {
    unblockPhrase(phraseToUnblock);
  }

  processQuestion(getFirstQuestion(phraseToUnblock));
}

/**
 * Unblocks the next question following the input element
 * @param {input DOM Element} el
 */
function unblockNextQuestion(el) {
  const nextQuestion = getNextQuestion(el);
  if (nextQuestion){
    unblockQuestion(nextQuestion);
  }

  processQuestion(nextQuestion);
}

/**
 * Blocks the next question following the input element
 * @param {input DOM Element} el
 */
function blockNextQuestions(el) {
  const toBlocks = getRemainingQuestions(el);
  for (const toBlock of toBlocks){
    blockQuestion(toBlock);
  }
}

/**
 * Blocks the next phrase following the input element
 * @param {input DOM Element} el
 */
function blockNextPhrases(el){
  const toBlocks = getRemainingPhrases(el);
  for (const toBlock of toBlocks){
    blockPhrase(toBlock);
  }
}

/**
 * Hides the continue button on the panel containing the element
 * @param {input DOM Element} el
 */
function blockNextPanel(el){
  const panelIndex = getPanelIndex(el);
  document
    .getElementsByClassName(`js-panel-${panelNumber(panelIndex)}-continue-button`)[0]
    .classList
    .add('hidden');
}

/**
 * Processes a question to determine if it will unblock others
 * @param {Vashon question} question
 */
function processQuestion(question){
  const inputs = getQuestionInputs(question);
  // If a question has no inputs, then unblock the next phrase regardless
  if (inputs[0] == null) { unblockNextPhrase(question); }
  // Else process each input
  [...inputs].forEach((input) => {
    const errors = validateField(input.name, conditionedValue(input));
    if (errors == null && input.value != 'default') {
      unblockElementsAfterInput(input);
    }
  });
}

function containsNonNumericalChars(string) {
  if (string.match(/[^0-9]/) !== null) {
    return true;
  } else {
    return false;
  }
}

/**
 * Returns the panel index for any element on the panel
 * @param {DOM Element} el
 */
function getPanelIndex(el){
  const panel = el.closest('.lead-constructor__panel');
  if (panel == null){
    return -1;
  }
  const panelId = panel.dataset.id;
    const splitElements = panelId.split('-');
    const lastElement = splitElements[splitElements.length - 1];
    // check `lastElement` string for any non-numerical values (ex. '2A')
    // if there are non-numerical values, ex '2A', return `lastElement` unchanged
    // `lastElement` can remain a string
    // (no need to subtract from it to use it in a class name)
    if (containsNonNumericalChars(lastElement)) {
      return lastElement;
    } else {
    return parseInt(lastElement) - 1;
  }
}

/**
 * True if the given input is the last in a question
 * @param {input DOM Element} el
 */
function isLastInput(el){
  const label = el.closest('.form__field');
  const question = el.closest('.vashon-question');
  const labels = question.querySelectorAll('.form__field');
  return label == labels[labels.length-1];
}

/**
 * True is the given input belongs to the last question in a phrase
 * @param {input DOM Element} el
 */
function isLastQuestion(el){
  const question = el.closest('.vashon-question');
  const nextTarget = getNextTargetId(question);
  return nextTarget == null || nextTarget == '';
}

/**
 * True if the given element belong to the last phrase on a panel
 * @param {input DOM Element} el
 */
function isLastPhrase(el) {
  const phrase = el.closest('.vashon-phrase');
  const phrases_container = el.closest('.vashon-phrase-container');
  const phrases = phrases_container.querySelectorAll('.vashon-phrase');
  return phrase == phrases[phrases.length-1];
}

function initializeVashon(){
  const panels = document.querySelectorAll(
    '.lead-constructor__panel[data-id^=vashon-panel]'
  );

  for (const panel of panels) {
    initializePanel(panel);
  }
}

// Initializes the panels
function initializePanel(panel){
  const phraseContainer = panel.querySelector('.vashon-phrase-container');
  initializePhraseContainer(phraseContainer);
}

// Initializes the Phrase Containers
function initializePhraseContainer(phraseContainer){
  blockNextPanel(phraseContainer);
  const phrases = phraseContainer.getElementsByClassName('vashon-phrase');

  for (const phrase of phrases) {
    let blocked = true;
    if(phrase == phrases[0]) { blocked=false; }
    initializePhrase(phrase, blocked);
  }
}

// Phrases should be initialized such that the first question is only visible
function initializePhrase(phrase, blocked=true){
  if (blocked) { blockPhrase(phrase); }

  const questions = phrase.getElementsByClassName('vashon-phrase__question');
  for (const question of questions) {
    let blocked = true;
    if(question == questions[0]) { blocked=false; }
    initializeQuestion(question, blocked);
  }
  // Add event listener for handling inputs
  phrase.addEventListener('keyup', handleChangeEvent);
  phrase.addEventListener('change', handleChangeEvent);
}

function initializeQuestion(question, blocked=true){
  if (blocked) { blockQuestion(question); }
}

function handleChangeEvent(event) {
  const input = event.target;
  if (input.value == 'default'){ return; }

  const errors = validateField(input.name, conditionedValue(input));
  blockElementsAfterInput(input);

  if (errors){ return getInvalidHandler(event)(input, errors); }
  handleNowValid(input);
  unblockElementsAfterInput(input);

  const scrollOffset =  -window.innerHeight * 0.4 - 0.6 * navbarHeight();

  if(input.tagName == 'SELECT') { scrollToElement(getNextInputInPanel(input), scrollOffset); }
}

// Returns a function to call to process invalidity
function getInvalidHandler(event){
  if(event.type == 'keyup'){
    return handleNowInvalidKeyup;
  } else if (event.type == 'change'){
    return handleNowInvalidChange;
  }
}

const validationState = {
  valid: 'valid',
  invalid: 'invalid',
  new: 'new'
};

const validateImmediately = [
  'test_score',
  'desired_score'
];

function getValidationState(el){
  if (el.classList.contains('js-input-validated')){
    if (el.classList.contains('input--error')){
      return validationState.invalid;
    }
    return validationState.valid;
  }
  return validationState.new;
}

function handleNowValid(el){
  const previousState = getValidationState(el);

  if(previousState == validationState.invalid || previousState == validationState.new){
    applyInputValid(el);
  }
}

// Change events can trigger an additional transition if the field
// is unfocused before becoming valid
function handleNowInvalidChange(el, errors){
  const previousState = getValidationState(el);

  if (previousState == validationState.new){
    if (conditionedValue(el).length > 0){
      applyInputError(el, errors[0]);
    }
  } else if (previousState == validationState.valid){
    applyInputError(el, errors[0]);
  } else if (previousState == validationState.invalid){
    changeErrorText(el, errors[0]);
  }
}

function handleNowInvalidKeyup(el, errors){
  const previousState = getValidationState(el);
  if (previousState == validationState.new){
    if (validateImmediately.indexOf(el.name) > -1 && conditionedValue(el).length > 0){
      applyInputError(el, errors[0]);
    }
  } else if (previousState == validationState.valid){
    applyInputError(el, errors[0]);
  } else if (previousState == validationState.invalid){
    changeErrorText(el, errors[0]);
  }
}

function togglePlacementText(validatedInput) {
  const mainQuestionElement = validatedInput.closest('.vashon-question__main--with-explanation');

  if (mainQuestionElement) {
    const hasValidationError = validatedInput.classList.contains('input--error');
    const classList = mainQuestionElement.querySelector('.vashon-explanation').classList;
    hasValidationError ? classList.add('hidden') : classList.remove('hidden');
  }
}

// Applies the results to the element indicating that it is valid
export function applyInputValid(input) {
  input.classList.remove('input--error');
  input.classList.add('js-input-validated');
  const message = input.parentNode.querySelector('.form__error');
  if(message) message.remove();
  togglePlacementText(input);
}


// Applies the results to the element indicating that it is invalid
export function applyInputError(input, message) {
  const el = document.createElement('span');
  el.className = 'form__error';
  el.innerHTML = message;
  input.classList.add('input--error');
  input.classList.add('js-input-validated');
  input.parentNode.appendChild(el);
  togglePlacementText(input);
}

// Changes the error text
export function changeErrorText(input, message) {
  const siblings = input.parentNode.childNodes;
  siblings[siblings.length - 1].innerHTML = message;
}
